import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { ApiException, useApi } from '@/hooks/useApi';
import { formatDateTime } from '@/utils/formatters';
import SortContext from '@/contexts/SortContext';
import I18N from '../I18N';
import StatusChip from '../StatusChip';
import NameWithHubspotId from './NameWithHubspotId';
import NotesColumn from './NotesColumn';
import SortLabel from './SortLabel';
import '@/styles/components/CompanyTable.css';
const CompanyTable = ({ defaultSize = 50, ownerId, searchParams, className, }) => {
    const { callApi } = useApi();
    const navigate = useNavigate();
    const { showError } = useSnackbar();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [size] = useState(defaultSize);
    const [total, setTotal] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const [orderBy, setOrderBy] = useState('name');
    const [descend, setDescend] = useState(false);
    const searchKey = useMemo(() => `${ownerId || ''}_${new URLSearchParams(searchParams).toString()}`, [ownerId, searchParams]);
    // Reset data when search params or ownerId change
    useEffect(() => {
        setCompanies([]);
        setPage(1);
        setHasMore(true);
        setTotal(-1);
    }, [searchKey]);
    const fetchCompanies = useCallback(async (pageNum, append = false) => {
        setLoading(true);
        try {
            const params = new URLSearchParams(searchParams);
            params.set('page', `${pageNum}`);
            params.set('page_size', `${size}`);
            params.set('descending', `${descend}`);
            params.set('order_by', orderBy);
            if (ownerId)
                params.set('owner_id', `${ownerId}`);
            const endpoint = `/companies?${params.toString()}`;
            const response = await callApi('GET', endpoint);
            if (!Array.isArray(response?.companies))
                throw new Error();
            setCompanies(prev => append ? [...prev, ...response.companies] : response.companies);
            setTotal(response.total);
            setHasMore(response.companies.length === size);
        }
        catch (e) {
            const detail = e instanceof ApiException && e.detail;
            showError(detail || 'companies.messages.loadError');
            setHasMore(false);
        }
        finally {
            setLoading(false);
        }
    }, [callApi, descend, orderBy, ownerId, searchParams, showError, size]);
    useEffect(() => {
        fetchCompanies(1, false);
    }, [fetchCompanies]);
    const loadMore = useCallback(() => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchCompanies(nextPage, true);
    }, [page, fetchCompanies]);
    if (loading && companies.length === 0) {
        return _jsx(CircularProgress, {});
    }
    return (_jsx(TableContainer, { component: Paper, id: "scrollableDiv", className: `table-container-scroll ${className || ''}`, "data-testid": "scrollable", "data-label": "table-container", children: _jsx(InfiniteScroll, { dataLength: companies.length, next: loadMore, hasMore: hasMore, loader: _jsx("div", { className: "table-loader", children: _jsx(CircularProgress, { size: 30 }) }), endMessage: _jsx("div", { className: "company-table-end-message", "data-label": "table-footer", children: total > 0 ? (_jsx(I18N, { children: "companies.showingAll" })) : (_jsx(I18N, { children: "companies.noneFound" })) }), scrollableTarget: "scrollableDiv", children: _jsxs(Table, { "aria-label": "company table", className: "company-table", "data-testid": "company-table", size: "small", children: [_jsx(TableHead, { children: _jsx(TableRow, { "data-label": "header", children: _jsxs(SortContext.Provider, { value: { orderBy, descend, setOrderBy, setDescend }, children: [_jsx(TableCell, { className: "name", children: _jsx(SortLabel, { field: "name", children: _jsx(I18N, { children: "companies.fields.company" }) }) }), _jsx(TableCell, { className: "name", children: _jsx(I18N, { children: "companies.fields.owner" }) }), _jsx(TableCell, { className: "status", children: _jsx(I18N, { children: "companies.fields.status" }) }), _jsx(TableCell, { className: "date", children: _jsx(SortLabel, { field: "createdAt", children: _jsx(I18N, { children: "metadata.createdAt" }) }) }), _jsx(TableCell, { className: "c-notes", children: _jsx(I18N, { children: "companies.fields.notes" }) })] }) }) }), _jsx(TableBody, { children: companies.map(company => (_jsxs(TableRow, { className: "company-table-row", onClick: () => navigate(`/companies/${company.id}`), children: [_jsx(TableCell, { className: "company", "data-testid": "company-table-cell-company", "data-label": "primary-info", children: _jsx(NameWithHubspotId, { name: company.name, hubspotId: company.hubspotId }) }), _jsx(TableCell, { className: "owner", "data-testid": "company-table-cell-owner", "data-label": "primary-info", children: _jsx(NameWithHubspotId, { name: company.owner?.name, hubspotId: company.owner?.hubspotId }) }), _jsx(TableCell, { className: "status", "data-testid": "company-table-cell-status", children: _jsx(StatusChip, { status: company.status }) }), _jsx(TableCell, { className: "created", "data-testid": "company-table-cell-updated", children: !company.createdAt ? (_jsx("small", { children: "-" })) : (formatDateTime(company.lastModifiedAt || company.createdAt)) }), _jsx(NotesColumn, { notes: company.notes, testId: "company-table-cell-notes" })] }, company.id))) })] }) }) }));
};
export default CompanyTable;
